
import { DataObj, KPI } from '@/contexts/AdminContext/dashboardProvider';

export const extractNumber = (value: any): number | string => {
    // If already a number, return as-is
    if (typeof value === 'number') {
        return value;
    }

    const str = String(value).trim();

    // Try direct number conversion first
    const directNum = Number(str);
    if (!isNaN(directNum)) {
        return directNum;
    }

    // Extract number using regex - handles various formats:
    // "$4.99", "USD 8.00", "500 KB", "25%", "1,234.56", "-123.45"
    const numberMatch = str.match(/[-+]?\s*[0-9,]+(?:\.[0-9]*)?/);
    if (numberMatch) {
        const numStr = numberMatch[0].replace(/[,\s]/g, ''); // Remove commas and spaces
        const extractedNum = parseFloat(numStr);
        if (!isNaN(extractedNum)) {
            return extractedNum;
        }
    }

    // If no number found, return original string
    return value;
};

// Helper to remove cycles from Sankey data (DAG requirement)
export const sanitizeSankeyData = (links: any[]) => {
    if (!links || links.length === 0) return [];

    // 1. Remove self-loops (A -> A)
    let safeLinks = links.filter(l => l.source !== l.target);

    // 2. Remove cycles (back-edges) using DFS
    const adj = new Map<string, string[]>();
    safeLinks.forEach(l => {
        if (!adj.has(l.source)) adj.set(l.source, []);
        adj.get(l.source)?.push(l.target);
    });

    const visited = new Set<string>();
    const recStack = new Set<string>();
    const badEdges = new Set<string>(); // "source|target"

    const hasCycle = (node: string) => {
        visited.add(node);
        recStack.add(node);

        const neighbors = adj.get(node) || [];
        for (const neighbor of neighbors) {
            if (!visited.has(neighbor)) {
                hasCycle(neighbor);
            } else if (recStack.has(neighbor)) {
                // Cycle detected: node -> neighbor is a back edge
                badEdges.add(`${node}|${neighbor}`);
            }
        }

        recStack.delete(node);
    };

    // Prioritize traversal order: Start node -> Nodes with 0 in-degree -> Others
    const nodes = new Set<string>();
    const inDegree = new Map<string, number>();
    
    safeLinks.forEach(l => {
        nodes.add(l.source);
        nodes.add(l.target);
        inDegree.set(l.target, (inDegree.get(l.target) || 0) + 1);
        if (!inDegree.has(l.source)) inDegree.set(l.source, 0);
    });

    const sortedNodes = Array.from(nodes).sort((a, b) => {
        if (a === 'Start') return -1;
        if (b === 'Start') return 1;
        return (inDegree.get(a) || 0) - (inDegree.get(b) || 0);
    });

    sortedNodes.forEach(node => {
        if (!visited.has(node)) {
            hasCycle(node);
        }
    });

    return safeLinks.filter(l => !badEdges.has(`${l.source}|${l.target}`));
};

export const formatDisplayValue = (value: number | string, format?: string, unit?: string): string => {
    if (value === null || value === undefined || value === '') return '-';

    if (format === 'string') {
        return String(value);
    }

    let numVal: number;
    if (typeof value === 'number') {
        numVal = value;
    } else {
        // Handle strings that might be numbers
        const parsed = parseFloat(String(value));
        if (isNaN(parsed)) return String(value);
        numVal = parsed;
    }

    switch (format) {
        case 'currency':
            const currencyStr = numVal.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
            if (unit) {
                const isSymbol = /^[^a-zA-Z0-9]$/.test(unit) || unit === '$' || unit === '¥' || unit === '£' || unit === '€';
                return isSymbol ? `${unit}${currencyStr}` : `${unit} ${currencyStr}`;
            }
            return currencyStr;

        case 'percent':
            const percentStr = Number.isInteger(numVal)
                ? numVal.toLocaleString()
                : numVal.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
            return `${percentStr}${unit || '%'}`;

        case 'number':
        default:
            const numStr = Number.isInteger(numVal)
                ? numVal.toLocaleString()
                : numVal.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

            return unit ? `${numStr} ${unit}` : numStr;
    }
};

export const formatCompactNumber = (value: number): string => {
    if (value === 0) return '0';
    const absValue = Math.abs(value);

    if (absValue >= 1000000000) {
        return `${(value / 1000000000).toFixed(1)}b`;
    }
    if (absValue >= 1000000) {
        return `${(value / 1000000).toFixed(1)}m`;
    }
    if (absValue >= 1000) {
        return `${(value / 1000).toFixed(1)}k`;
    }
    return value.toFixed(1);
};

export const getFormattedKPIValue = (kpi: KPI) => {
    if (!kpi.data || typeof kpi.data === 'string') return (kpi as any)?.value || '0';

    const dataObj = kpi.data as DataObj;
    if (!Array.isArray(dataObj) || dataObj.length === 0) return '0';

    const rawVal = dataObj[0][kpi.column];
    if (rawVal === undefined || rawVal === null) return '0';

    const val = extractNumber(rawVal);

    return formatDisplayValue(val, kpi.format, kpi.unit);
};
